import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Send } from "lucide-react";
import { listingsApi, type ListingDetailDto } from "@/api/buyers";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useRecordDrawerParam } from "@/components/layout/EntityRecordDrawer";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button, OverflowMenu, Timestamp, useToast } from "@/design-system/components/ui";
import { listingKeys } from "@/hooks/buyers/buyerKeys";
import { useOptionalAuth } from "@/hooks/useAuth";
import { safeHref } from "@/design-system/lib/safeHref";
import {
  ListingDetailBody,
  isListingTabKey,
  listingTitle,
  type TabKey,
} from "./ListingDetailBody";
import { PresentBuyerWizard } from "./presentBuyer/PresentBuyerWizard";
import { SubmissionRecordDrawer } from "./recordDrawers";

/**
 * The listing detail ROUTE.
 *
 * <p>Thin by design. Everything below the page chrome is `ListingDetailBody`,
 * which the associated-listings drawer renders too — so a listing reads the
 * same whether it was opened as a page or glanced at from a buyer's shortlist,
 * and a fix to one is a fix to both.</p>
 *
 * <p>This wrapper owns the three things that only make sense on a full page: the
 * `PageHeader` chrome, the tab as a URL parameter so "that listing's documents"
 * is a link rather than an instruction, and the record DRAWER a submission row
 * opens into. The drawer that renders this same body keeps its tab in local
 * state instead, and has no drawer slot of its own.</p>
 */
export function ListingDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  // Self-gating via `useOptionalAuth` — the ConvertToBorrowerButton precedent.
  // No auth context resolved = not permitted, which fails closed.
  const auth = useOptionalAuth();
  const canManage = Boolean(auth?.hasPermission("Listings.Manage"));
  // Presenting a buyer is a SUBMISSION write, not a listing one — the same
  // permission the submissions list gates its own "Present a buyer" button on.
  const canPresent = Boolean(auth?.hasPermission("Submissions.Manage"));
  const [presenting, setPresenting] = useState(false);

  // The open submission, as URL state — the `?payment=` contract every
  // allocation drawer in the app runs on. A refresh reopens it, and the address
  // bar points at what is actually being read.
  const submissionDrawer = useRecordDrawerParam("submission");
  // The row's own label, so the drawer is titled the instant it opens rather
  // than after a fetch. Null on a cold arrival at a `?submission=` URL, where
  // the generic noun is the honest answer — this page holds no list to look the
  // record up in, and the body inside reports what it actually finds.
  const [openSubmissionLabel, setOpenSubmissionLabel] = useState<string | null>(null);

  // 🔴 Parsed here, at read time, rather than defaulted by a post-mount effect
  // that rewrites the query string: an effect that sets `?tab=overview` on
  // arrival re-keys every query this page owns and fetches the record twice.
  // An unknown value falls back to Overview.
  const raw = params.get("tab") ?? "";
  const tab: TabKey = isListingTabKey(raw) ? raw : "overview";
  const setTab = (next: TabKey) => {
    const p = new URLSearchParams(params);
    if (next === "overview") p.delete("tab");
    else p.set("tab", next);
    // 🔴 A drawer belongs to the tab that opened it. Leaving Buyers presented
    // with one open would otherwise carry it to Documents and paint a drawer
    // nobody asked for over a tab that has nothing to do with it. The buyer
    // page has dropped its record parameters on a tab change since the drawers
    // shipped; this page was the one that never did.
    p.delete("submission");
    setParams(p, { replace: true });
  };

  // The same query key the body reads, so React Query serves both from one
  // cache entry and one request. The header needs the record for its title and
  // its Edit action; the body owns the loading / failed / not-found states, and
  // the header simply does not render until there is something to head.
  const listing = useQuery({
    queryKey: listingKeys.detail(id),
    queryFn: () => listingsApi.get(id),
    enabled: Boolean(id),
  });
  const l = listing.data;

  /*
    `null` while loading is load-bearing — the alternative is a crumb that reads
    as one record's name and then changes to another.

    The leaf goes through `listingTitle` — the SAME resolution the header uses,
    rather than a second copy of it inline — so a blind listing (which
    deliberately has none) names itself the same way in the trail as it does
    everywhere else, instead of falling to the raw GUID the fallback would
    otherwise produce. A breadcrumb has room for a name and not for a
    marketplace pitch, which is the other half of what that shared helper now
    settles.
  */
  useBreadcrumbs(
    l ? [{ label: "Listings", href: "/listings" }, { label: listingTitle(l) }] : null,
  );

  return (
    <>
      {l && (
        <ListingPageHeader
          listing={l}
          canManage={canManage}
          onPresent={canPresent ? () => setPresenting(true) : undefined}
        />
      )}

      {/* Mounted at PAGE level, beside the header that opens it, so presenting a
          buyer is reachable from every tab rather than only from the one that
          lists the results. The body keeps its own copy for the drawer, where
          there is no header to hang the action on. */}
      {canPresent && l && (
        <PresentBuyerWizard
          open={presenting}
          onClose={() => setPresenting(false)}
          listingId={l.id}
          listingName={listingTitle(l)}
          /*
            The submission this creates belongs to THIS listing, and its drawer
            is already mounted a few lines below — so navigating away would
            unmount the page the specialist is working on to show them a record
            they could have read in place. `open` writes `?submission=`, which
            the drawer reads.

            The label is left unset deliberately: the drawer degrades to
            "Submission" until the invalidated read returns the buyer's name,
            which is a beat of vagueness rather than a wrong name.
          */
          onCreated={(submissionId) => submissionDrawer.open(submissionId)}
        />
      )}

      <ListingDetailBody
        listingId={id}
        activeTab={tab}
        onTabChange={setTab}
        onPresent={canPresent ? () => setPresenting(true) : undefined}
        onOpenSubmission={(submissionId, buyerName) => {
          setOpenSubmissionLabel(buyerName);
          submissionDrawer.open(submissionId);
        }}
      />

      {/* Keyed on the record, so opening a second submission starts it at
          Overview instead of inheriting the previous one's tab, and closing
          unmounts the body rather than leaving its queries subscribed behind a
          hidden drawer. */}
      {submissionDrawer.openId && (
        <SubmissionRecordDrawer
          key={submissionDrawer.openId}
          submissionId={submissionDrawer.openId}
          title={openSubmissionLabel ?? undefined}
          subtitle={l ? listingTitle(l) : "Submission"}
          onClose={() => {
            setOpenSubmissionLabel(null);
            submissionDrawer.close();
          }}
        />
      )}
    </>
  );
}

/**
 * The page's chrome.
 *
 * <p>🔴 <b>There is no whole-record EDIT button here any more.</b> Layout
 * Standard §4.1a.1 (v1.5) narrows the Overview rule to workflow actions and rules
 * that correcting a field belongs where that field is displayed — so each of
 * Overview's six cards carries its own pencil and its own modal, scoped to the
 * fields that card shows. Between them the six cover every field this button's
 * form carried, which is what made removing it safe rather than a regression;
 * <c>listing/ListingSectionEdit.test.tsx</c> pins that coverage against the
 * form's own field set.</p>
 *
 * <p>Two ways in would have been worse than either alone: a reader who fixed a
 * ZIP code in the header form and a reader who fixed it on the card would have
 * been using two different instruments to write the same column, and only one of
 * them sends a narrow patch.</p>
 */
function ListingPageHeader({
  listing: l,
  canManage,
  onPresent,
}: {
  listing: ListingDetailDto;
  canManage: boolean;
  onPresent?: () => void;
}) {
  // R17 — a stored URL becomes a link only through safeHref, as the Source
  // card's does: a `javascript:` value must never reach an href, and research
  // can now write this column.
  const originalHref = l.rawUrl ? safeHref(l.rawUrl) : null;
  return (
    <PageHeader
      eyebrow="Listing"
      title={listingTitle(l)}
      description={
        <>
          {[l.industry, l.city, l.state].filter(Boolean).join(" · ") || null}
          <ListingMetaLine listing={l} />
        </>
      }
      actions={
        <div className="flex flex-wrap items-center justify-end gap-space-3">
          {originalHref && (
            <a
              href={originalHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-space-1 text-small text-gold-shadow hover:underline"
            >
              <ExternalLink size={13} strokeWidth={1.5} aria-hidden />
              Open the original listing
            </a>
          )}
          <AgentRecruitmentAction listing={l} canManage={canManage} />
          {/*
            🔴 The page's ONE gold primary, and the reason it is this one: a
            listing exists to be put in front of buyers. Editing the record is
            maintenance; presenting somebody is the job. It was previously
            reachable only from inside the Buyers-presented tab, which is the
            surface a specialist is least likely to be standing on at the moment
            they decide — they are reading the financials.
          */}
          {onPresent && (
            <Button size="sm" onClick={onPresent} data-testid="listing-header-present">
              <Send size={14} strokeWidth={1.5} aria-hidden /> Present a buyer
            </Button>
          )}
        </div>
      }
    />
  );
}

/**
 * Escalating this listing's agent into broker recruitment.
 *
 * <p>🔴 <b>It lives in the HEADER because it MINTS A BROKER LEAD.</b> Layout
 * Standard §4.1a — an Overview may summarise, reference and link, but may not
 * mutate or send — and this was a create sitting in the Overview rail's agent
 * panel. §4.1a exempts the header in full, as persistent chrome present on every
 * tab rather than Overview body, and the boundary it draws is literal:
 * everything above the tab strip is header.</p>
 *
 * <p>Two consequences, both intended. It is now reachable from every tab rather
 * than only from Overview. And it is ABSENT FROM THE RECORD DRAWER, which has no
 * header — a listing opened over a buyer's shortlist can be read without a
 * control that recruits a broker being one stray press away from whatever the
 * reader was actually doing. That is the specific harm §4.1a names.</p>
 *
 * <p>The Overview rail still SAYS the agent is not in recruitment, and still
 * says why it matters, because that is a fact a specialist needs before they
 * promise a buyer anything. It just no longer offers to change it.</p>
 *
 * <p>Renders nothing at all when there is no agent, when the agent is already
 * escalated, or for a reader who cannot manage the listing — three separate
 * reasons the action does not apply, and an `OverflowMenu` with no items is
 * worse than no menu.</p>
 */
function AgentRecruitmentAction({
  listing: l,
  canManage,
}: {
  listing: ListingDetailDto;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const agent = l.listingAgent;
  // 🔴 Held from the MUTATION's OWN ANSWER rather than waited for. The endpoint
  // hands back the lead it created, and discarding it is a defect this codebase
  // has already fixed once here: the toast named a record with no way to reach
  // it, and the link appeared only if and when the background refetch landed.
  // "It worked, here it is" has to be true the moment the button stops spinning,
  // and moving this control into the header must not quietly undo that.
  const [createdLeadId, setCreatedLeadId] = useState<string | null>(null);

  const escalate = useMutation({
    mutationFn: () => listingsApi.escalateAgent(agent!.id, { reason: null }),
    onSuccess: (updated) => {
      setCreatedLeadId(updated.escalatedToLeadId ?? null);
      // The Overview rail's Recruitment field flips to its own lead link off
      // this read — the header does the deed and Overview reports it, which is
      // the shape §4.1a asks for.
      void qc.invalidateQueries({ queryKey: listingKeys.detail(l.id) });
      toast.success(
        "Escalated into broker recruitment. A Broker Marketer will work the lead.",
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (createdLeadId) {
    return (
      // 🔴 A new tab, not a `Link`, for the reason the rail's own lead link
      // gives: the lead lives in another module with no record drawer, and a
      // router navigation would throw away the listing the specialist is
      // standing on — along with any drawer open over it.
      <a
        href={`/leads/${createdLeadId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-space-1 text-small text-gold-shadow hover:underline"
        data-testid="listing-created-lead"
      >
        <ExternalLink size={13} strokeWidth={1.5} aria-hidden />
        Open the broker lead
      </a>
    );
  }

  if (!canManage || !agent || agent.escalatedToLeadId) return null;

  return (
    <OverflowMenu
      label="More listing actions"
      data-testid="listing-overflow"
      items={[
        {
          label: escalate.isPending
            ? "Escalating…"
            : "Escalate the agent into broker recruitment…",
          onSelect: () => escalate.mutate(),
        },
      ]}
    />
  );
}

/**
 * When this record was added, and when it was last touched.
 *
 * <p>Listings are shared and long-lived, and much of what is on one arrived by
 * paste from a marketplace page that may itself be months old. "Added in March,
 * untouched since" is the difference between a figure worth quoting to a buyer
 * and one worth re-checking first, and the page had nowhere that said it —
 * `createdAt` was buried in the Source card and `modifiedAt` was on the wire but
 * rendered nowhere at all.</p>
 */
function ListingMetaLine({ listing: l }: { listing: ListingDetailDto }) {
  return (
    // A `span`, not a `div`: `PageHeader` renders its description inside a `<p>`,
    // and a block element in there is invalid nesting that React reports at
    // runtime. `Timestamp` renders a `<time>` for the same reason.
    <span className="block mt-space-1 text-caption text-gray-mid">
      <Timestamp value={l.createdAt} prefix="Added" />
      {l.modifiedAt && (
        <>
          {" · "}
          <Timestamp value={l.modifiedAt} prefix="updated" />
        </>
      )}
    </span>
  );
}

export type { ListingDetailDto };
